import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import {
  getUser,
  adminListExplorerFolders,
  adminListExplorerFiles,
  adminGetExplorerFolderTree,
} from "../../api/admin.api";
import { VirtualFileList } from "../explorer/VirtualFileList";
import { VirtualFileGrid } from "../explorer/VirtualFileGrid";
import { PreviewModal } from "../preview/PreviewModal";
import { Spinner } from "../ui/Spinner";

const ADMIN_PAGE_SIZE = 50;
import type { ExplorerItem, FileItem, SortField, SortOrder } from "@titancloud/shared";

type ViewMode = "list" | "grid";

function noopRename(_id: string, _kind: "file" | "folder", _name: string): void {}
function noopDelete(_id: string, _kind: "file" | "folder", _name: string): void {}

export default function AdminUserFileExplorer() {
  const { userId, folderId } = useParams<{ userId: string; folderId?: string }>();
  const navigate = useNavigate();
  const currentFolderId = folderId ?? null;

  const [sort, setSort] = useState<SortField>("date");
  const [order, setOrder] = useState<SortOrder>("desc");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const userQuery = useQuery({
    queryKey: ["admin", "user", userId],
    queryFn: () => getUser(userId!),
    enabled: !!userId,
  });

  const foldersQuery = useQuery({
    queryKey: ["admin", "explorer", "folders", userId, currentFolderId],
    queryFn: () => adminListExplorerFolders(userId!, currentFolderId),
    enabled: !!userId,
  });

  const filesQuery = useInfiniteQuery({
    queryKey: ["admin", "explorer", "files", userId, currentFolderId, sort, order],
    queryFn: ({ pageParam }) =>
      adminListExplorerFiles(userId!, {
        folderId: currentFolderId,
        sort,
        order,
        limit: ADMIN_PAGE_SIZE,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!userId,
  });

  const treeQuery = useQuery({
    queryKey: ["admin", "explorer", "tree", userId, folderId],
    queryFn: () => adminGetExplorerFolderTree(userId!, folderId!),
    enabled: !!userId && !!folderId,
  });

  const allLoadedFiles = useMemo(
    () => filesQuery.data?.pages.flatMap((p) => p.files) ?? [],
    [filesQuery.data]
  );
  const totalFilesCount = filesQuery.data?.pages[0]?.total ?? 0;

  const allItems: ExplorerItem[] = useMemo(
    () => [
      ...(foldersQuery.data ?? []).map((f) => ({
        ...f,
        kind: "folder" as const,
        isDeleted: f.isDeleted ?? false,
        deletedAt: f.deletedAt ?? null,
      })),
      ...allLoadedFiles.map((f) => ({
        ...f,
        kind: "file" as const,
      })),
    ],
    [foldersQuery.data, allLoadedFiles]
  );

  const fileItems = allItems.filter((i): i is FileItem => i.kind === "file");
  const openPreview = (fileId: string) => {
    const idx = fileItems.findIndex((f) => f.id === fileId);
    if (idx !== -1) setPreviewIndex(idx);
  };

  const onFolderNavigate = (id: string) => {
    navigate(`/app/admin/users/${userId}/folder/${id}`);
  };

  const handleSort = (field: SortField) => {
    if (sort === field) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setSort(field);
      setOrder("desc");
    }
  };

  const SortButton = ({ field, label }: { field: SortField; label: string }) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className={`flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors ${
        sort === field
          ? "bg-amber-900/50 text-amber-100"
          : "text-amber-700/90 hover:text-amber-900 dark:text-amber-200/80 dark:hover:text-amber-50"
      }`}
    >
      {label}
      {sort === field && (
        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="currentColor">
          {order === "asc" ? <path d="M6 2l4 6H2l4-6z" /> : <path d="M6 10L2 4h8l-4 6z" />}
        </svg>
      )}
    </button>
  );

  const isLoading = foldersQuery.isLoading || filesQuery.isLoading;
  const basePath = userId ? `/app/admin/users/${userId}` : "";

  const targetUser = userQuery.data;

  if (!userId) {
    return (
      <div className="p-6 text-center text-gray-400">Missing user.</div>
    );
  }

  if (userQuery.isError) {
    return (
      <div className="p-6 text-center text-red-400">User not found.</div>
    );
  }

  return (
    <div className="flex h-full flex-col border-t-4 border-amber-500 bg-amber-950/25">
      {/* Sticky read-only banner */}
      <div className="sticky top-0 z-10 border-b border-amber-600/40 bg-amber-950/90 px-4 py-3 backdrop-blur-sm md:px-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-400">
              Read-only — another user&apos;s files
            </p>
            <p className="mt-0.5 text-sm text-amber-100/90">
              {targetUser ? (
                <>
                  <span className="font-medium">{targetUser.displayName}</span>
                  <span className="text-amber-200/70"> · {targetUser.email}</span>
                </>
              ) : (
                <span className="text-amber-200/70">Loading user…</span>
              )}
            </p>
            <p className="mt-1 text-xs text-amber-300/80">
              You cannot rename, delete, or upload here. Download and preview only.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to={`/app/admin/users/${userId}/deleted-files`}
              className="inline-flex shrink-0 items-center justify-center rounded-lg border border-red-800/50 bg-red-950/40 px-3 py-2 text-sm font-medium text-red-300 hover:bg-red-900/40"
            >
              Deleted Files
            </Link>
            <Link
              to="/app/admin"
              className="inline-flex shrink-0 items-center justify-center rounded-lg border border-amber-600/50 bg-amber-950/50 px-3 py-2 text-sm font-medium text-amber-100 hover:bg-amber-900/40"
            >
              Back to admin
            </Link>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4 md:p-6">
        {/* Breadcrumb */}
        <nav className="mb-3 flex flex-wrap items-center gap-1 text-sm text-gray-400">
          <Link
            to={`${basePath}/files`}
            className="text-amber-400/90 hover:text-amber-300 hover:underline"
          >
            {targetUser?.displayName ?? "Files"}
          </Link>
          {treeQuery.data?.map((seg) => (
            <span key={seg.id} className="flex items-center gap-1">
              <span className="text-gray-600">/</span>
              <Link
                to={`${basePath}/folder/${seg.id}`}
                className="text-amber-400/90 hover:text-amber-300 hover:underline"
              >
                {seg.name}
              </Link>
            </span>
          ))}
        </nav>

        {/* Toolbar: sort + view only */}
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-900/40 bg-gray-900/60 px-3 py-2">
          <span className="text-xs text-amber-200/60">Sort</span>
          <div className="flex items-center gap-1 rounded-lg border border-amber-900/50 bg-amber-950/30 p-1">
            <SortButton field="name" label="Name" />
            <SortButton field="size" label="Size" />
            <SortButton field="date" label="Date" />
          </div>
          <div className="ml-auto flex rounded-lg border border-amber-900/50 bg-amber-950/30">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`p-1.5 transition-colors ${
                viewMode === "grid" ? "text-amber-100" : "text-amber-600/70 hover:text-amber-200"
              }`}
              title="Grid view"
            >
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
                <path d="M1 2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1V2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H7a1 1 0 01-1-1V2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 01-1-1V2zM1 7a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1V7zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H7a1 1 0 01-1-1V7zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 01-1-1V7zM1 12a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1v-2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H7a1 1 0 01-1-1v-2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 01-1-1v-2z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`p-1.5 transition-colors ${
                viewMode === "list" ? "text-amber-100" : "text-amber-600/70 hover:text-amber-200"
              }`}
              title="List view"
            >
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M2 4.5A.5.5 0 012.5 4h11a.5.5 0 010 1h-11a.5.5 0 01-.5-.5zm0 4a.5.5 0 01.5-.5h11a.5.5 0 010 1h-11a.5.5 0 01-.5-.5zm0 4a.5.5 0 01.5-.5h11a.5.5 0 010 1h-11a.5.5 0 01-.5-.5z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-amber-900/35 bg-gray-950/80">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : allItems.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-amber-200/50">
              <svg className="h-12 w-12 text-amber-700/50" viewBox="0 0 48 48" fill="none">
                <path
                  d="M8 12a4 4 0 014-4h10l4 4h14a4 4 0 014 4v20a4 4 0 01-4 4H12a4 4 0 01-4-4V12z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
              <p className="text-sm">This folder is empty</p>
            </div>
          ) : viewMode === "grid" ? (
            <VirtualFileGrid
              items={allItems}
              onOpen={openPreview}
              onRename={noopRename}
              onDelete={noopDelete}
              readOnly
              explorerUserId={userId}
              onFolderNavigate={onFolderNavigate}
              hasNextPage={filesQuery.hasNextPage}
              isFetchingNextPage={filesQuery.isFetchingNextPage}
              onLoadMore={() => void filesQuery.fetchNextPage()}
            />
          ) : (
            <VirtualFileList
              items={allItems}
              onRename={noopRename}
              onDelete={noopDelete}
              readOnly
              explorerUserId={userId}
              onFolderNavigate={onFolderNavigate}
              hasNextPage={filesQuery.hasNextPage}
              isFetchingNextPage={filesQuery.isFetchingNextPage}
              onLoadMore={() => void filesQuery.fetchNextPage()}
            />
          )}

          {!isLoading && totalFilesCount > 0 && (
            <div className="flex-shrink-0 border-t border-amber-900/30 bg-amber-950/20 px-4 py-2.5 flex items-center justify-between gap-4">
              <span className="text-xs text-amber-200/50">
                Showing{" "}
                <span className="text-amber-100/80 font-medium">{allLoadedFiles.length}</span>
                {" "}of{" "}
                <span className="text-amber-100/80 font-medium">{totalFilesCount}</span>
                {" "}file{totalFilesCount !== 1 ? "s" : ""}
              </span>
              {filesQuery.isFetchingNextPage && (
                <span className="flex items-center gap-2 text-xs text-amber-200/50">
                  <Spinner size="sm" />
                  Loading more…
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {viewMode === "grid" && previewIndex !== null && fileItems.length > 0 && userId && (
        <PreviewModal
          files={fileItems}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
          explorerUserId={userId}
          hasMore={filesQuery.hasNextPage}
          isLoadingMore={filesQuery.isFetchingNextPage}
          onLoadMore={() => void filesQuery.fetchNextPage()}
        />
      )}
    </div>
  );
}
